import { Skeleton } from '@chakra-ui/react';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import React from 'react';
import { Switch, Redirect, Route } from 'react-router-dom';

import { useGetUserQuery } from 'types/dashboard-core-gqlTypes.generated.d';

import { PortfolioBorrowingBaseRoute } from './PortfolioBorrowingBaseRoute';
import { PortfolioCovenantsRoute } from './PortfolioCovenantsRoute';
import { PortfolioCreditAgreementRoute } from './PortfolioCreditAgreementRoute';
import { PortfolioOverviewFacilitiesRoute } from './PortfolioOverviewFacilitiesRoute';
import { PortfolioOverviewRiskRoute } from './PortfolioOverviewRiskRoute';
import { PortfolioProfitLossRoute } from './PortfolioProfitLossRoute';
import { PortfolioReceivableDetailsRoute } from './PortfolioReceivableDetailsRoute';
import { PortfolioReceivablesRoute } from './PortfolioReceivablesRoute';
import { PortfolioReportBuilderRoute } from './PortfolioReportBuilderRoute';
import { PortfolioStratsRoute } from './PortfolioStratsRoute';
import { PortfolioSummaryRoute } from './PortfolioSummaryRoute';
import { AthenaSchemaProvider } from 'packages/organization/hooks/AthenaSchemaContext';

export const PortfolioRouter = withLDConsumer()(({ flags }) => {
  const { data, loading } = useGetUserQuery();

  if (!data || !data.user || loading) {
    return <Skeleton w="100%" h="100%" />;
  }

  const {
    user: { organizationId },
  } = data;

  return (
    /**
     * Schema provider does not block children from loading
     * Provider is wrapped around the whole portfolio router to trigger
     * loading of athena schema as soon as portfolio section is loaded to prevent double loading
     *
     * Double loading will not be avoided for direct consumers of the context
     * Context consumers will have to handle loading state themselves using the context provided `AtheneSchemaContextLoadingWrapper`
     */
    <AthenaSchemaProvider organizationId={organizationId}>
      <Switch>
        <Route
          exact
          path="/portfolio/overview/facilities"
          component={PortfolioOverviewFacilitiesRoute}
        />
        <Route
          exact
          path="/portfolio/overview/risk"
          component={PortfolioOverviewRiskRoute}
        />
        <Route
          exact
          path="/portfolio/summary"
          component={PortfolioSummaryRoute}
        />
        {flags?.displayLenderPortfolioMonitoringCovenants && (
          <Route
            exact
            path="/portfolio/covenants"
            component={PortfolioCovenantsRoute}
          />
        )}
        <Route
          exact
          path="/portfolio/borrowing-base"
          component={PortfolioBorrowingBaseRoute}
        />
        <Route
          exact
          path="/portfolio/profit-loss"
          component={PortfolioProfitLossRoute}
        />
        <Route
          exact
          path="/portfolio/receivables"
          component={PortfolioReceivablesRoute}
        />
        <Route
          exact
          path="/portfolio/receivables/:finleyReceivableId"
          component={PortfolioReceivableDetailsRoute}
        />
        <Route
          exact
          path="/portfolio/credit-agreement"
          component={PortfolioCreditAgreementRoute}
        />
        {flags?.showLenderPortfolioStrats &&
          flags?.showLenderPortfolioStrats !== 'hide' && (
            <Route
              exact
              path="/portfolio/strats"
              component={PortfolioStratsRoute}
            />
          )}
        {flags?.showExploReportBuilder && (
          <Route
            exact
            path="/portfolio/report-builder"
            component={PortfolioReportBuilderRoute}
          />
        )}
        <Route path="/portfolio">
          <Redirect from="/portfolio" to="/portfolio/overview/facilities" />
        </Route>
      </Switch>
    </AthenaSchemaProvider>
  );
});
